// AndroidBackButton — 안드로이드 시스템 뒤로 버튼 전역 처리 (설계 docs/specs/2026-09-11-android-v5-back-edge-design.md §2-3)
// 판정은 lib/androidBack.ts(순수 함수·단위 테스트), 여기는 등록과 실행만 한다.
// SwipeBack과 같은 자리(routes/index.tsx 라우터 안)에 둔다 — useLocation·useBackTo가 필요하다.
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useBackTo } from '../hooks/useBackTo';
import { decideAndroidBack, topDismissibleOverlay } from '../lib/androidBack';
import { probeMark } from './HistoryProbe'; // 계측 빌드에서만 동작(평소 no-op)

export default function AndroidBackButton() {
  const back = useBackTo();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // 리스너는 한 번만 등록하고 최신 경로·함수는 ref로 읽는다.
  //   경로가 바뀔 때마다 재등록하면 해제→등록 사이 틈에 뒤로가 들어와 Capacitor 기본 goBack()으로 샌다.
  const latest = useRef({ back, navigate, pathname });
  useEffect(() => {
    latest.current = { back, navigate, pathname };
  });

  useEffect(() => {
    // iOS엔 시스템 뒤로 버튼이 없다. 웹에선 브라우저가 처리한다. 안드로이드에서만 등록.
    if (Capacitor.getPlatform() !== 'android') return undefined;

    const handle = App.addListener('backButton', () => {
      const { back: goBack, navigate: nav, pathname: path } = latest.current;
      const overlay = topDismissibleOverlay(document);
      const action = decideAndroidBack(path, overlay !== null);
      probeMark(`androidBack ${action} at ${path}`);

      if (action === 'dismiss') {
        // 오버레이 클릭을 대신 눌러 그 모달의 기존 onClose를 태운다(공통 컴포넌트 무수정).
        (overlay as HTMLElement).click();
      } else if (action === 'minimize') {
        void App.minimizeApp();
      } else if (action === 'home') {
        // replace — 탭 루트 기록을 홈으로 대체해 이후 홈에서 뒤로가 곧바로 앱 나가기가 되게 한다.
        nav('/', { replace: true });
      } else {
        goBack();
      }
    });

    return () => {
      void handle.then((h) => h.remove());
    };
  }, []);

  return null;
}
